'use strict';

import {TeejitRestClient} from './teejit-rest-client.js';

const profileAvatarOptions = {
    sky: {background: '#e8f5ff', foreground: '#3698db'},
    mint: {background: '#eaf7f0', foreground: '#26996b'},
    coral: {background: '#fff1ee', foreground: '#d95f48'},
    violet: {background: '#f4efff', foreground: '#7862d8'},
    slate: {background: '#edf2f6', foreground: '#465564'},
    sun: {background: '#fff7df', foreground: '#c98500'},
};

const escapeSvgText = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const profileInitials = (userData = {}) => {
    const firstName = String(userData.personal_first_name || userData.first_name || '').trim();
    const lastName = String(userData.personal_last_name || userData.last_name || '').trim();
    const email = String(userData.email || '').trim();
    let initials = '';

    if (firstName) {
        initials += firstName.charAt(0);
    }
    if (lastName) {
        initials += lastName.charAt(0);
    }
    if (!initials && email) {
        initials = email.charAt(0);
    }

    return (initials || window.khThemeRuntime?.profileInitials || 'P').toUpperCase();
};

const profileAvatarDataUri = (slug, userData = {}) => {
    const option = profileAvatarOptions[slug] || profileAvatarOptions.sky;
    const initials = escapeSvgText(profileInitials(userData));
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160" role="img" aria-label="Avatar">'
        + '<rect width="160" height="160" rx="80" fill="' + option.background + '"/>'
        + '<circle cx="80" cy="61" r="28" fill="' + option.foreground + '" opacity=".18"/>'
        + '<path d="M37 132c7.5-24.5 23.5-36.8 43-36.8s35.5 12.3 43 36.8" fill="' + option.foreground + '" opacity=".16"/>'
        + '<text x="80" y="92" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="42" font-weight="800" fill="' + option.foreground + '">' + initials + '</text>'
        + '</svg>';

    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

export const resolveTeejitProfileImage = (value, userData = {}) => {
    const image = String(value || '').trim();
    if (!image) {
        return '';
    }
    if (image.startsWith('kh-avatar:')) {
        return profileAvatarDataUri(image.substring('kh-avatar:'.length), userData);
    }
    if (/^(https?:)?\/\//i.test(image) || image.startsWith('data:image/') || image.startsWith('blob:') || image.startsWith('/')) {
        return image;
    }
    const storageBaseUrl = String(window.khThemeRuntime?.storageBaseUrl || '').replace(/\/+$/, '');
    return storageBaseUrl ? storageBaseUrl + '/' + image.replace(/^\/+/, '') : image;
};

export const updateTeejitProfileAvatarNodes = (profileImageUrl) => {
    const imageUrl = String(profileImageUrl || '').trim();
    if (!imageUrl) {
        return;
    }

    const frames = Array.from(document.querySelectorAll('[data-profile-nav-avatar-frame]'));
    frames.forEach((frame) => {
        let image = frame.querySelector('img[data-profile-nav-avatar]');
        if (!image) {
            frame.textContent = '';
            image = document.createElement('img');
            image.setAttribute('alt', '');
            image.setAttribute('width', '40');
            image.setAttribute('height', '40');
            image.setAttribute('loading', 'lazy');
            image.setAttribute('decoding', 'async');
            image.setAttribute('data-profile-nav-avatar', '');
            frame.appendChild(image);
        }
        image.setAttribute('src', imageUrl);
    });

    document.querySelectorAll('img[data-profile-nav-avatar]').forEach((image) => {
        image.setAttribute('src', imageUrl);
    });

    if (window.khThemeRuntime) {
        window.khThemeRuntime.userProfileImage = imageUrl;
    }
};

export class TeejitSessionWarmup {
    constructor({endpoint, restClient} = {}) {
        this.endpoint = String(endpoint || window.khThemeRuntime?.sessionWarmupEndpoint || 'user/session/warmup');
        this.restClient = restClient || new TeejitRestClient();
    }

    warmup() {
        if (window.khSessionWarmupPromise) {
            return window.khSessionWarmupPromise;
        }

        const controller = typeof window.AbortController === 'function'
            ? new window.AbortController()
            : null;
        window.khSessionWarmupController = controller;

        const path = this.endpoint.indexOf('http') === 0 ? this.endpoint : this.endpoint.replace(/^\/+/, '');
        window.khSessionWarmupPromise = fetch(this.endpoint.indexOf('http') === 0 ? this.endpoint : this.restClient.url(path), {
            method: 'POST',
            credentials: 'same-origin',
            headers: this.restClient.headers(true),
            body: JSON.stringify({
                access_token: this.restClient.accessToken,
                ...(this.restClient.getFrontendLocale() ? {frontend_locale: this.restClient.getFrontendLocale()} : {}),
            }),
            ...(controller ? {signal: controller.signal} : {}),
        }).then((response) => response.json().catch(() => ({})).then((payload) => {
            if (!response.ok || !payload || payload.success === false) {
                throw new Error('kh_session_warmup_failed');
            }

            this.applyRuntimeUserData(payload);
            window.dispatchEvent(new CustomEvent('kh:sessionWarmupReady', {detail: payload}));
            return payload;
        })).catch((error) => {
            window.khSessionWarmupPromise = null;
            throw error;
        }).finally(() => {
            if (window.khSessionWarmupController === controller) {
                window.khSessionWarmupController = null;
            }
        });

        return window.khSessionWarmupPromise;
    }

    applyRuntimeUserData(payload) {
        const userData = payload?.user_data || {};
        const userId = userData?.id;
        if (!window.khThemeRuntime) {
            return;
        }

        if (userId) {
            const normalizedUserId = String(userId);
            window.khThemeRuntime.userId = normalizedUserId;
            if (window.khThemeRuntime.knowledgeHubTour) {
                window.khThemeRuntime.knowledgeHubTour.userId = normalizedUserId;
            }
        }

        const imageKeys = ['personal_profile_image', 'profile_image', 'avatar', 'profile_avatar'];
        const dynamicImageKeys = Object.keys(userData).filter((key) => key.indexOf('profile_image') !== -1);
        const profileImage = imageKeys.concat(dynamicImageKeys)
            .map((key) => userData[key])
            .find((value) => typeof value === 'string' && value.trim());
        const profileImageUrl = resolveTeejitProfileImage(profileImage, userData);
        if (profileImageUrl) {
            updateTeejitProfileAvatarNodes(profileImageUrl);
        }

        const path = String(window.location?.pathname || '');
        if (userData.imported_user_reset_pw && !path.includes('/my-profile') && !path.includes('/login')) {
            window.location.href = '/teejit/my-profile/?action=account&edit=1';
        }
    }

    static bootFromRuntime() {
        if (!window.khThemeRuntime?.sessionNeedsWarmup) {
            return null;
        }

        const client = new TeejitRestClient({
            restBaseUrl: window.khThemeRuntime.restBaseUrl,
            accessToken: window.khThemeRuntime.userAccessToken,
            nonce: window.khThemeRuntime.restNonce,
        });

        return new TeejitSessionWarmup({
            endpoint: window.khThemeRuntime.sessionWarmupEndpoint,
            restClient: client,
        }).warmup().catch(() => null);
    }

    static abortPending() {
        if (window.khSessionWarmupController && typeof window.khSessionWarmupController.abort === 'function') {
            window.khSessionWarmupController.abort('frontend-locale-change');
        }
        window.khSessionWarmupController = null;
        window.khSessionWarmupPromise = null;
    }
}

if (typeof document !== 'undefined' && !document.documentElement.dataset.khSessionWarmupAbortBound) {
    document.documentElement.dataset.khSessionWarmupAbortBound = '1';
    document.addEventListener('kh:frontendLocaleChanging', () => {
        TeejitSessionWarmup.abortPending();
    });
}
